import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, howToSchema, toolSchema } from "@/lib/schema";
import { SoundControls } from "@/components/SoundControls";

const CardMatch = lazy(() => import("@/components/games/CardMatch").then((m) => ({ default: m.CardMatch })));
const AchievementWall = lazy(() => import("@/components/games/AchievementWall").then((m) => ({ default: m.AchievementWall })));

const TITLE = "Palworld TCG Memory Match — Card Flip Game";
const DESCRIPTION =
  "Free Palworld card memory match game: Normal, Hard and Expert boards with 8 or 12 pairs of real TCG card scans, animated flips and badges.";
const OG_IMAGE = "https://palstars.com/og/palworld-games.jpg";

const STEPS = [
  { name: "Pick Normal, Hard or Expert", text: "Normal deals 8 pairs and leaves a mismatch visible for three quarters of a second; Hard and Expert deal 12 pairs at the same larger card size, with Expert using the fastest peek." },
  { name: "Deal the board", text: "Tiles are dealt face down from random official Palworld TCG cards, so no two boards are the same." },
  { name: "Flip two tiles", text: "Each tap flips a tile with an animated turn and a flip sound; two tiles stay up while the pair is checked." },
  { name: "Keep the pairs", text: "A matching pair chimes and locks face up, a miss flips both back after a beat — remember the card codes you saw." },
  { name: "Clear in fewer flips", text: "Par is 40 flips on Normal and 62 on Hard. Clearing any board unlocks Card Memoriser; 22 flips or fewer on Normal — or any Hard clear — unlocks Photographic Deck." },
];

const FAQ: FaqItem[] = [
  { q: "What is the Palworld card memory match game?", a: "TCG Memory Match is a free Palworld card flip game on Pal Stars: pairs of real official card scans are dealt face down and you clear the board by remembering where each printing sits." },
  { q: "What is the difference between Normal, Hard and Expert memory match?", a: "Normal deals 8 pairs (16 tiles) with a 750 millisecond peek and a par of 40 flips. Hard and Expert each deal 12 pairs (24 tiles) at the same card size and par at 62 flips; Expert shortens the mismatch peek to 360 milliseconds." },
  { q: "Are the cards real Palworld TCG cards?", a: "Yes. Every tile uses an official Palworld Trading Card Game scan with its printed English code, the same images as the card database and price hub." },
  { q: "Does the memory match game have sound and animation?", a: "Yes — each tile turns with a flip animation and plays a flip cue, matched pairs chime, and clearing the board plays a win sting. The volume control on this page applies sitewide." },
  { q: "Does it pay booster packs?", a: "No. The memory match is cosmetic: it unlocks browser-stored badges only. Free booster packs come from the rewards board and free spins from Palsino." },
  { q: "How many boards can I play per day?", a: "Twenty boards per browser each UTC day, which keeps score and share-link spam down while leaving plenty of practice runs." },
];

export const Route = createFileRoute("/card-match")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld TCG Memory Match — flip real card scans" },
       { property: "og:description", content: "8 or 12 pairs of official Palworld cards, animated flips and sound. Can you clear the expert board?" },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/card-match" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/card-match" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Games", "/palworld-games"], ["TCG Memory Match", "/card-match"]]),
      toolSchema({ name: "Palworld TCG Memory Match", description: DESCRIPTION, path: "/card-match" }),
      howToSchema({ name: "How to play the Palworld card memory match game", description: "Choose Normal or Hard, deal the board, flip two tiles at a time, keep the pairs and clear it in fewer flips.", path: "/card-match", steps: STEPS }),
      faqSchema(FAQ, "https://palstars.com/card-match"),
    ],
  }),
  component: CardMatchPage,
});

function CardMatchPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars Arcade · TCG game</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld TCG memory match</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          A free Palworld card memory match game: 8 pairs on Normal or 12 on Hard and Expert, all real official TCG scans face
          down, animated flips with sound on every tap and badges for clearing them fast.
        </p>
        <SoundControls className="mt-4 justify-start" label="match sound" />
      </header>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Palworld TCG memory match</h2><p className="mt-2 text-sm text-muted-foreground">Shuffling the board…</p></section>}>
        <CardMatch />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">How to play</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} id={`step-${i + 1}`} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Achievement badges</h2><p className="mt-2 text-sm text-muted-foreground">Loading badges…</p></section>}>
        <AchievementWall game="card-match" heading="Memory match badges" />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Play next</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/palworld-games" className="font-semibold text-primary hover:underline">All free Palworld games →</Link></li>
          <li><Link to="/pal-catch" className="font-semibold text-primary hover:underline">Palworld Pal Sphere toss →</Link> animated catching game.</li>
          <li><Link to="/cards" className="font-semibold text-primary hover:underline">Palworld card database →</Link> every official scan.</li>
          <li><Link to="/price-guess" className="font-semibold text-primary hover:underline">Palworld card price quiz →</Link></li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld card memory match FAQ" />
    </div>
  );
}
